/**
 * El catálogo de paletas.
 *
 * Son las 30 de `web/src/styles/palettes.css`, en el mismo orden y con los
 * mismos ids: quien elige una aquí, allá encuentra sus cuatro colores. Va en
 * `shared/` porque el servidor también lo consulta, para validar lo que le
 * llega en el tema de una aplicación (`sanitizeTheme` en `server/routes.ts`).
 *
 * Los grupos son dos y su orden es el de las pestañas del selector:
 */
export type PaletteGroup = "vivid" | "pastel";

export interface PaletteInfo {
  /** El id que viaja en `data-palette`. */
  id: string;
  /** Cómo se llama en el selector. */
  name: string;
  group: PaletteGroup;
}

/** El nombre de cada grupo, tal como se lee en el selector. */
export const PALETTE_GROUPS: readonly { id: PaletteGroup; label: string }[] = [
  { id: "vivid", label: "Vivos" },
  { id: "pastel", label: "Pasteles" },
];

export const PALETTES: readonly PaletteInfo[] = [
  { id: "electrico", name: "Eléctrico", group: "vivid" },
  { id: "tropico", name: "Trópico", group: "vivid" },
  { id: "frambuesa", name: "Frambuesa", group: "vivid" },
  { id: "bosque", name: "Bosque neón", group: "vivid" },
  { id: "lava", name: "Lava", group: "vivid" },
  { id: "cyberpunk", name: "Cyberpunk", group: "vivid" },
  { id: "v-solar", name: "Solar", group: "vivid" },
  { id: "v-naranja", name: "Naranja", group: "vivid" },
  { id: "v-ambar", name: "Ámbar", group: "vivid" },
  { id: "v-lima", name: "Lima", group: "vivid" },
  { id: "v-esmeralda", name: "Esmeralda", group: "vivid" },
  { id: "v-turquesa", name: "Turquesa", group: "vivid" },
  { id: "v-oceano", name: "Océano", group: "vivid" },
  { id: "v-violeta", name: "Violeta", group: "vivid" },
  { id: "v-carmin", name: "Carmín", group: "vivid" },

  { id: "algodon", name: "Algodón", group: "pastel" },
  { id: "durazno", name: "Durazno", group: "pastel" },
  { id: "cielo", name: "Cielo", group: "pastel" },
  { id: "salvia", name: "Salvia", group: "pastel" },
  { id: "lavanda", name: "Lavanda", group: "pastel" },
  { id: "vainilla", name: "Vainilla", group: "pastel" },
  { id: "p-coral", name: "Coral", group: "pastel" },
  { id: "p-limon", name: "Limón", group: "pastel" },
  { id: "p-pistacho", name: "Pistacho", group: "pastel" },
  { id: "p-menta", name: "Menta", group: "pastel" },
  { id: "p-aguamarina", name: "Aguamarina", group: "pastel" },
  { id: "p-nube", name: "Nube", group: "pastel" },
  { id: "p-lila", name: "Lila", group: "pastel" },
  { id: "p-chicle", name: "Chicle", group: "pastel" },
  { id: "p-cuarzo", name: "Cuarzo", group: "pastel" },
] as const;

/**
 * Las «Monocromos» que hubo: un color principal y los tres neutros de
 * `custom`, o sea, una paleta personalizada con ese color. Se quitaron del
 * catálogo y `normalizePalette()` lee las guardadas como `custom` con su
 * color, para que nadie vea cambiar su aplicación.
 */
export const LEGACY_MONO_COLORS: Readonly<Record<string, string>> = {
  "mono-red": "#fb2c36",
  "mono-orange": "#ff6900",
  "mono-amber": "#fe9a00",
  "mono-yellow": "#f0b100",
  "mono-lime": "#7ccf00",
  "mono-green": "#00c950",
  "mono-emerald": "#00bc7d",
  "mono-teal": "#00bba7",
  "mono-sky": "#00a6f4",
  "mono-blue": "#2b7fff",
  "mono-indigo": "#615fff",
  "mono-violet": "#8e51ff",
  "mono-purple": "#ad46ff",
  "mono-pink": "#f6339a",
  "mono-rose": "#ff2056",
  "mono-slate": "#62748e",
};

/** El id de la paleta escrita a mano. */
export const CUSTOM_PALETTE = "custom";

const IDS = new Set(PALETTES.map((p) => p.id));

/** Si es un id del catálogo (el `custom` no cuenta: va aparte). */
export function isPaletteId(id: unknown): id is string {
  return typeof id === "string" && IDS.has(id);
}
